import { BOTONES } from '../../../../../componentes/botones';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatStepperModule } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  Agrupacion,
  Dia,
  IActivo,
  ICategoriaEvento,
  IConfigEventoUsuario,
  ICreateConfigEventoUsuario,
  IFilter,
  IGrupo,
  IListado,
  IListadoCategoria,
  IQueryParam,
  IUbicacion,
  IUsuario,
} from 'modelos/src';
import {
  Observable,
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { ConfigEventoUsuarioService } from '../../../../../auxiliares/servicios/http/config-evento-usuario.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { CardHeaderComponent } from '../../../../../componentes/card-header/card-header.component';
import { InfoBannerComponent } from '../../../../../componentes/info-banner/info-banner.component';
import { SectionTitleComponent } from '../../../../../componentes/section-title/section-title.component';
import { TimePickerComponent } from '../../../../../componentes/time-picker/time-picker.component';
import { DiasSemanaSelectorComponent } from '../../../../../componentes/dias-semana-selector/dias-semana-selector.component';
import { frecuenciasAdmitidas } from '../../../../../auxiliares/frecuencias-regla.util';
import {
  DatePickerComponent,
  DateRangeValue,
} from '../../../../../componentes/date-picker/date-picker.component';
import { ListadoCategoriasSelectorComponent } from '../../../../../componentes/selectores/listado-categoria-selector/listado-categoria-selector.component';
import { ListadoCategoriasService } from '../../../../../auxiliares/servicios/http/listado-categorias.service';
import {
  cronogramaLocalAUtc,
  cronogramaUtcALocal,
} from '../../../../../auxiliares/cronograma-utc.util';

export type TipoReglaVehiculo =
  | 'Velocidad'
  | 'Exceso de velocidad por calle'
  | 'Ubicación'
  | 'Detenido'
  | 'Recorridos'
  | 'Divergencia GPS';

export interface ICrearReglaVehiculoData {
  /** Vehículo preseleccionado: fija el alcance a "Entidad" y lo bloquea. */
  activo?: IActivo;
  /** Alternativa a `activo` cuando sólo se tiene el id. */
  idEntidad?: string;
  /** Grupo preseleccionado: fija el alcance a "Grupo". */
  idGrupo?: string;
  /** Regla existente: el wizard abre en modo edición. */
  edit?: IConfigEventoUsuario;
}

/** Valores de `frecuencia` del modelo que el wizard ofrece. */
type FrecuenciaRegla =
  | 'Continua'
  | 'Unica'
  | 'Continua en un periodo'
  | 'Unica en un periodo'
  | 'Cronograma';

interface IOpcionFrecuencia {
  valor: FrecuenciaRegla;
  label: string;
  hint: string;
}

const OPCIONES_FRECUENCIA: IOpcionFrecuencia[] = [
  {
    valor: 'Continua',
    label: 'Siempre',
    hint: 'Genera un evento cada vez que se cumple la condición.',
  },
  {
    valor: 'Unica',
    label: 'Sólo la primera vez',
    hint: 'Genera un único evento y la regla se desactiva.',
  },
  {
    valor: 'Continua en un periodo',
    label: 'Durante un período',
    hint: 'Genera eventos sólo entre las fechas elegidas.',
  },
  {
    valor: 'Unica en un periodo',
    label: 'Sólo la primera vez dentro de un período',
    hint: 'Genera un único evento entre las fechas elegidas y la regla se desactiva.',
  },
  {
    valor: 'Cronograma',
    label: 'En ciertos días y horarios',
    hint: 'Fuera de estos días y horarios la regla no genera eventos ni notificaciones. Si "Hasta" es anterior a "Desde", el horario termina al día siguiente.',
  },
];

const DIAS_HABILES: Dia[] = [
  'Lunes',
  'Martes',
  'Miercoles',
  'Jueves',
  'Viernes',
];

interface IOpcionRegla {
  tipo: TipoReglaVehiculo;
  titulo: string;
  descripcion: string;
  icon: string;
}

@Component({
  selector: 'app-crear-regla-vehiculo',
  templateUrl: './crear-regla-vehiculo.component.html',
  styleUrls: ['./crear-regla-vehiculo.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    BOTONES,
    MatIconModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    
    MatDividerModule,
    NgSelectModule,
    CardHeaderComponent,
    InfoBannerComponent,
    SectionTitleComponent,
    TimePickerComponent,
    DatePickerComponent,
    ListadoCategoriasSelectorComponent,
    DiasSemanaSelectorComponent,
  ],
})
export class CrearReglaVehiculoComponent implements OnInit, OnDestroy {
  public dialogData = inject<ICrearReglaVehiculoData>(MAT_DIALOG_DATA, {
    optional: true,
  });
  private dialogRef = inject<
    MatDialogRef<CrearReglaVehiculoComponent, IConfigEventoUsuario | undefined>
  >(MatDialogRef, { optional: true });
  private service = inject(ConfigEventoUsuarioService);
  private listadoCategoriasService = inject(ListadoCategoriasService);
  private lista2 = inject(Lista2Service);
  public helper = inject(HelperService);

  /** Abre el wizard como dialog. Devuelve el ref para suscribirse a `afterClosed`. */
  public static open(
    formularios: FormularioDialogService,
    data?: ICrearReglaVehiculoData,
  ): MatDialogRef<
    CrearReglaVehiculoComponent,
    IConfigEventoUsuario | undefined
  > {
    return formularios.abrir<
      CrearReglaVehiculoComponent,
      ICrearReglaVehiculoData | undefined,
      IConfigEventoUsuario | undefined
    >(CrearReglaVehiculoComponent, { tamano: 'grande', data });
  }

  public loading = false;
  public form!: FormGroup;

  /** Regla que se está editando (undefined = alta). */
  public edit?: IConfigEventoUsuario;

  /** Los vehículos generan eventos por medio de su tracker. */
  public readonly categoriaTipoEvento = 'Evento-Tracker';
  public listadoCategoriaInitialNames: string[] = [];

  /** Vehículo fijado al abrir desde un vehículo concreto. */
  public vehiculoFijado?: IActivo;
  public alcanceBloqueado = false;

  public opcionesRegla: IOpcionRegla[] = [
    {
      tipo: 'Velocidad',
      titulo: 'Exceso de velocidad (fija)',
      descripcion: 'Avisar cuando el vehículo supere una velocidad.',
      icon: 'speed',
    },
    {
      tipo: 'Exceso de velocidad por calle',
      titulo: 'Exceso de velocidad (por calle)',
      descripcion: 'Avisar según el límite legal de la calle o ruta.',
      icon: 'add_road',
    },
    {
      tipo: 'Ubicación',
      titulo: 'Entra / sale de una zona',
      descripcion: 'Avisar al entrar o salir de una ubicación o geocerca.',
      icon: 'location_on',
    },
    {
      tipo: 'Detenido',
      titulo: 'Detenido demasiado tiempo',
      descripcion: 'Avisar si el vehículo permanece detenido X minutos.',
      icon: 'timer',
    },
    {
      tipo: 'Recorridos',
      titulo: 'Se desvía del recorrido',
      descripcion: 'Avisar si se aleja del recorrido asignado.',
      icon: 'alt_route',
    },
    {
      tipo: 'Divergencia GPS',
      titulo: 'Divergencia GPS',
      descripcion: 'Avisar ante divergencia de GPS sostenida.',
      icon: 'gps_off',
    },
  ];

  // Listados
  public grupos: IGrupo[] = [];
  public ubicaciones: IUbicacion[] = [];
  public categoriasEvento: ICategoriaEvento[] = [];
  public usuarios: IUsuario[] = [];

  // Búsqueda asíncrona de vehículos (alcance "Entidad" sin preselección)
  public searchTermVehiculos$ = new Subject<string>();
  public vehiculos$!: Observable<IActivo[]>;
  public vehiculos: IActivo[] = [];
  public loadingVehiculos = false;

  private subs: Subscription[] = [];

  // ===== Getters de condición =====
  get alcance(): Agrupacion {
    return this.form?.get('alcance')?.value;
  }
  get tipoRegla(): TipoReglaVehiculo | null {
    return this.form?.get('tipoRegla')?.value;
  }
  get velocidadForm(): FormGroup {
    return this.form?.get('velocidad') as FormGroup;
  }
  get excesoCalleForm(): FormGroup {
    return this.form?.get('excesoVelocidadCalle') as FormGroup;
  }
  get ubicacionForm(): FormGroup {
    return this.form?.get('ubicacion') as FormGroup;
  }
  get recorridosForm(): FormGroup {
    return this.form?.get('recorridos') as FormGroup;
  }
  get cronogramaForm(): FormGroup {
    return this.form?.get('cronograma') as FormGroup;
  }
  get frecuencia(): FrecuenciaRegla {
    return this.form?.get('frecuencia')?.value ?? 'Continua';
  }
  /** Sólo las frecuencias que el backend de esta regla respeta. */
  get opcionesFrecuencia(): IOpcionFrecuencia[] {
    if (!this.tipoRegla) return [];
    const permitidas = frecuenciasAdmitidas(this.tipoRegla);
    return OPCIONES_FRECUENCIA.filter((o) => permitidas.includes(o.valor));
  }
  get admiteFrecuencia(): boolean {
    return this.opcionesFrecuencia.length > 1;
  }
  get hintFrecuencia(): string {
    return (
      OPCIONES_FRECUENCIA.find((o) => o.valor === this.frecuencia)?.hint ?? ''
    );
  }
  get usaCronograma(): boolean {
    return this.admiteFrecuencia && this.frecuencia === 'Cronograma';
  }
  get usaPeriodo(): boolean {
    return (
      this.admiteFrecuencia &&
      (this.frecuencia === 'Continua en un periodo' ||
        this.frecuencia === 'Unica en un periodo')
    );
  }
  get periodoValido(): boolean {
    if (!this.usaPeriodo) return true;
    const p: DateRangeValue | null = this.form.get('periodo')?.value;
    return (
      !!p?.start &&
      !!p?.end &&
      new Date(p.start).getTime() < new Date(p.end).getTime()
    );
  }
  /** "Hasta" anterior a "Desde" es válido: la ventana termina al día
   *  siguiente (p. ej. 22:00 a 06:00). */
  get cronogramaValido(): boolean {
    if (!this.usaCronograma) return true;
    const c = this.cronogramaForm.getRawValue();
    return (
      !!c.dias?.length &&
      !!c.horaInicio &&
      !!c.horaFin &&
      c.horaInicio !== c.horaFin
    );
  }

  // ===== Validez por paso =====
  get paso1Valido(): boolean {
    if (this.alcance === 'Global') return true;
    if (this.alcance === 'Entidad') return !!this.form?.get('idEntidad')?.value;
    if (this.alcance === 'Grupo') return !!this.form?.get('idGrupo')?.value;
    return false;
  }
  get paso2Valido(): boolean {
    return !!this.tipoRegla;
  }
  get paso3Valido(): boolean {
    switch (this.tipoRegla) {
      case 'Velocidad':
        return this.velocidadForm?.get('superior a')?.value > 0;
      case 'Exceso de velocidad por calle':
        return this.excesoCalleForm?.get('tolerancia')?.value >= 0;
      case 'Ubicación':
        return (
          !!this.ubicacionForm?.get('idUbicacion')?.value &&
          (this.ubicacionForm?.get('dentro')?.value ||
            this.ubicacionForm?.get('fuera')?.value)
        );
      case 'Detenido':
        return this.form?.get('detenido')?.value > 0;
      case 'Recorridos':
        return (
          this.recorridosForm?.get('distancia')?.value > 0 &&
          (this.recorridosForm?.get('dentro')?.value ||
            this.recorridosForm?.get('fuera')?.value)
        );
      case 'Divergencia GPS':
        return true;
      default:
        return false;
    }
  }
  get formValido(): boolean {
    return (
      this.paso1Valido &&
      this.paso2Valido &&
      this.paso3Valido &&
      this.cronogramaValido &&
      this.periodoValido
    );
  }

  /**
   * Reafirma el permiso acá independientemente de que quien abrió el wizard
   * ya lo haya chequeado: cada pantalla que abre `CrearReglaVehiculoComponent`
   * debería filtrar el botón, pero si alguna no lo hace (o deja de hacerlo),
   * esto evita que se pueda crear la regla igual.
   */
  get puedeCrear(): boolean {
    return this.helper.tienePermiso(
      this.edit
        ? 'Administración - Editar eventos personalizados'
        : 'Administración - Crear eventos personalizados',
    );
  }

  /** Tipo de regla a partir de la condición guardada (modo edición). */
  private tipoReglaDe(config?: IConfigEventoUsuario): TipoReglaVehiculo | null {
    const a = config?.condicion?.activo;
    if (!a) return null;
    if (a.velocidad) return 'Velocidad';
    if (a.excesoVelocidadCalle) return 'Exceso de velocidad por calle';
    if (a.ubicacion) return 'Ubicación';
    if (a.detenido) return 'Detenido';
    if (a.recorridos) return 'Recorridos';
    if (a.divergenciaGPS) return 'Divergencia GPS';
    return null;
  }

  private createForm(): void {
    const e = this.edit;
    const a = e?.condicion?.activo;
    this.vehiculoFijado = this.dialogData?.activo;
    const idEntidad =
      this.dialogData?.activo?._id ||
      this.dialogData?.idEntidad ||
      e?.idEntidad ||
      null;
    const idGrupo = this.dialogData?.idGrupo || e?.idGrupo || null;

    let alcance: Agrupacion = e?.agrupacion ?? 'Entidad';
    if (!e && idEntidad) {
      alcance = 'Entidad';
      this.alcanceBloqueado = true;
    } else if (!e && idGrupo) {
      alcance = 'Grupo';
      this.alcanceBloqueado = true;
    }

    // El cronograma viene en UTC: se edita en la hora local del usuario.
    const cronograma = cronogramaUtcALocal({
      dias: e?.dias,
      horaInicio: e?.horaInicio,
      horaFin: e?.horaFin,
    });
    const frecuencia = (e?.frecuencia as FrecuenciaRegla) || 'Continua';

    this.form = new FormGroup({
      alcance: new FormControl<Agrupacion>(alcance, Validators.required),
      idEntidad: new FormControl<string | null>(idEntidad),
      idGrupo: new FormControl<string | null>(idGrupo),
      tipoRegla: new FormControl<TipoReglaVehiculo | null>(
        this.tipoReglaDe(e),
        Validators.required,
      ),
      // Parámetros de cada tipo de regla
      velocidad: new FormGroup({
        'superior a': new FormControl<number>(
          a?.velocidad?.['superior a'] ?? 60,
        ),
        tiempoMinimo: new FormControl<number | null>(
          a?.velocidad?.tiempoMinimo ?? null,
        ),
      }),
      excesoVelocidadCalle: new FormGroup({
        tolerancia: new FormControl<number>(
          a?.excesoVelocidadCalle?.tolerancia ?? 10,
        ),
        umbralFallback: new FormControl<number | null>(
          a?.excesoVelocidadCalle?.umbralFallback ?? null,
        ),
        tiempoMinimo: new FormControl<number | null>(
          a?.excesoVelocidadCalle?.tiempoMinimo ?? null,
        ),
      }),
      ubicacion: new FormGroup({
        idUbicacion: new FormControl<string | null>(
          a?.ubicacion?.idUbicacion ?? null,
        ),
        dentro: new FormControl(a?.ubicacion ? !!a.ubicacion.dentro : true),
        fuera: new FormControl(a?.ubicacion ? !!a.ubicacion.fuera : true),
      }),
      detenido: new FormControl<number>(a?.detenido?.['mas de'] ?? 15),
      recorridos: new FormGroup({
        distancia: new FormControl<number>(a?.recorridos?.distancia ?? 100),
        dentro: new FormControl(a?.recorridos?.dentro !== false),
        fuera: new FormControl(a?.recorridos?.fuera !== false),
      }),
      // Cuándo genera eventos
      frecuencia: new FormControl<FrecuenciaRegla>(frecuencia),
      periodo: new FormControl<DateRangeValue | null>(
        e?.validaDesde && e?.validaHasta
          ? { start: e.validaDesde, end: e.validaHasta }
          : null,
      ),
      cronograma: new FormGroup({
        dias: new FormControl<Dia[]>(
          cronograma.dias?.length ? cronograma.dias : DIAS_HABILES,
        ),
        horaInicio: new FormControl<string | null>(
          cronograma.horaInicio || '08:00',
        ),
        horaFin: new FormControl<string | null>(cronograma.horaFin || '18:00'),
      }),
      // Comunes
      idListadoCategoria: new FormControl<string | null>(
        e?.idListadoCategoria ?? null,
      ),
      idCategoriaEvento: new FormControl<string | null>(
        e?.idCategoriaEvento ?? null,
      ),
      notificar: new FormControl(e ? !!e.notificar : true),
      atender: new FormControl(e ? !!e.atender : false),
      idsUsuarios: new FormControl<string[] | null>(e?.idsUsuarios ?? null),
    });

    if (this.vehiculoFijado) {
      this.vehiculos = [this.vehiculoFijado];
    } else if (e?.activo) {
      // Edición: el vehículo actual queda cargado en el buscador.
      this.vehiculos = [e.activo];
    }

    // Si la regla nueva no admite la frecuencia elegida, vuelve a "Siempre".
    this.subs.push(
      this.form.get('tipoRegla')!.valueChanges.subscribe(() => {
        const permitidas = this.opcionesFrecuencia.map((o) => o.valor);
        if (!permitidas.includes(this.frecuencia)) {
          this.form.get('frecuencia')?.setValue('Continua');
        }
      }),
    );
  }

  public onListadoCategoriaChange(cat: IListadoCategoria | null): void {
    this.form.get('idListadoCategoria')?.setValue(cat?._id ?? null);
  }

  public seleccionarRegla(tipo: TipoReglaVehiculo): void {
    this.form.get('tipoRegla')?.setValue(tipo);
  }

  private configurarBusquedaVehiculos(): void {
    this.vehiculos$ = this.searchTermVehiculos$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 3) {
          this.loadingVehiculos = false;
          return of(this.vehiculos);
        }
        this.loadingVehiculos = true;
        const filter: IFilter<IActivo> = {
          identificacion: { $regex: term, $options: 'i' },
        };
        const query: IQueryParam = {
          includeChildren: true,
          childrenLevel: 0,
          filter: JSON.stringify(filter),
        };
        return this.lista2.vehiculos(query).pipe(
          map((data: IListado<IActivo>) => {
            this.loadingVehiculos = false;
            return data.datos;
          }),
        );
      }),
      // Muestra el vehículo precargado (edición) antes de la primera búsqueda.
      startWith(this.vehiculos),
    );
  }

  public buscarVehiculos(term: string): void {
    this.searchTermVehiculos$.next(term);
  }

  private async listarGrupos(): Promise<void> {
    const query: IQueryParam = {
      select: 'nombre idCliente',
      sort: 'nombre',
      includeChildren: true,
    };
    const data = await firstValueFrom(this.lista2.grupos(query, 30));
    this.grupos = data.datos;
  }

  private async listarUbicaciones(): Promise<void> {
    const filter: IFilter<IUbicacion> = {
      categoria: {
        $in: ['Zona', 'Vehiculos', 'Hospital', 'Centro de Atención'],
      },
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: 'identificacion',
      sort: 'identificacion',
    };
    const data = await firstValueFrom(this.lista2.ubicacions(query, 30));
    this.ubicaciones = data.datos;
  }

  private async listarCategorias(): Promise<void> {
    const data = await firstValueFrom(this.lista2.categoriaEventos({}, 30));
    this.categoriasEvento = data.datos;
  }

  private async listarUsuarios(): Promise<void> {
    const query: IQueryParam = {
      select: 'usuario datosPersonales.telefono',
      sort: 'usuario',
    };
    const data = await firstValueFrom(this.lista2.usuarios(query, 30));
    this.usuarios = data.datos;
  }

  public searchUsuario = (term: string, item: IUsuario): boolean => {
    const t = term.toLowerCase();
    return (
      !!item.usuario?.toLowerCase().includes(t) ||
      !!item.datosPersonales?.nombre?.toLowerCase().includes(t) ||
      !!item.datosPersonales?.telefono?.toLowerCase().includes(t)
    );
  };

  /**
   * Arma la config. En una edición los campos que dejan de aplicar viajan en
   * `null` para que se borren (mismo criterio que el formulario de Eventos
   * personalizados).
   */
  private getData(): ICreateConfigEventoUsuario {
    const f = this.form.getRawValue();
    const frecuencia: FrecuenciaRegla = this.admiteFrecuencia
      ? f.frecuencia
      : 'Continua';
    const data: ICreateConfigEventoUsuario = {
      tipoEntidad: 'Vehiculo',
      agrupacion: f.alcance,
      tipoEnvio: 'Notificacion Push',
      frecuencia,
      notificar: f.notificar,
      atender: f.atender,
      idListadoCategoria: f.idListadoCategoria || null,
      idCategoriaEvento: f.idCategoriaEvento || null,
      idsUsuarios: f.notificar ? f.idsUsuarios || null : null,
      idEntidad: f.alcance === 'Entidad' ? f.idEntidad : null,
      idGrupo: f.alcance === 'Grupo' ? f.idGrupo : null,
      condicion: this.buildCondicion(),
      generarSoloUnaVez:
        frecuencia === 'Unica' || frecuencia === 'Unica en un periodo'
          ? true
          : null,
      generarSiNoSeCumple: null,
      validaDesde: null,
      validaHasta: null,
      dias: null,
      horaInicio: null,
      horaFin: null,
    } as ICreateConfigEventoUsuario;
    if (!this.edit) data.activa = true;

    if (this.usaPeriodo) {
      const p: DateRangeValue = f.periodo;
      data.validaDesde = new Date(p.start).toISOString();
      data.validaHasta = new Date(p.end).toISOString();
    }
    // El backend guarda y compara el cronograma en UTC: el usuario lo carga
    // en su hora local.
    if (this.usaCronograma) {
      const utc = cronogramaLocalAUtc({
        dias: f.cronograma.dias,
        horaInicio: f.cronograma.horaInicio,
        horaFin: f.cronograma.horaFin,
      });
      data.dias = utc.dias;
      data.horaInicio = utc.horaInicio;
      data.horaFin = utc.horaFin;
    }
    return data;
  }

  private buildCondicion(): ICreateConfigEventoUsuario['condicion'] {
    const f = this.form.getRawValue();
    switch (this.tipoRegla) {
      case 'Velocidad':
        return {
          activo: {
            velocidad: {
              'superior a': f.velocidad['superior a'],
              tiempoMinimo: f.velocidad.tiempoMinimo || undefined,
            },
          },
        };
      case 'Exceso de velocidad por calle':
        return {
          activo: {
            excesoVelocidadCalle: {
              tolerancia: f.excesoVelocidadCalle.tolerancia,
              umbralFallback:
                f.excesoVelocidadCalle.umbralFallback || undefined,
              tiempoMinimo: f.excesoVelocidadCalle.tiempoMinimo || undefined,
            },
          },
        };
      case 'Ubicación':
        return {
          activo: {
            ubicacion: {
              idUbicacion: f.ubicacion.idUbicacion,
              dentro: f.ubicacion.dentro,
              fuera: f.ubicacion.fuera,
            },
          },
        };
      case 'Detenido':
        return { activo: { detenido: { 'mas de': f.detenido } } };
      case 'Recorridos':
        return {
          activo: {
            recorridos: {
              distancia: f.recorridos.distancia,
              dentro: f.recorridos.dentro,
              fuera: f.recorridos.fuera,
            },
          },
        };
      case 'Divergencia GPS':
        return { activo: { divergenciaGPS: true } };
      default:
        return {};
    }
  }

  public async onSubmit(): Promise<void> {
    if (!this.formValido) return;
    if (!this.puedeCrear) {
      this.helper.notifError(
        this.edit
          ? 'No tenés permiso para editar reglas de evento'
          : 'No tenés permiso para crear reglas de evento',
      );
      return;
    }
    this.loading = true;
    try {
      const data = this.getData();
      const res = this.edit?._id
        ? await this.service.update(this.edit._id, data)
        : await this.service.create(data);
      this.helper.notifSuccess(
        this.edit ? 'Regla actualizada' : 'Regla creada correctamente',
      );
      this.volver(res);
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    }
    this.loading = false;
  }

  public volver(res?: IConfigEventoUsuario): void {
    if (this.dialogRef) {
      this.dialogRef.close(res);
    } else {
      HelperService.volver();
    }
  }

  /** Nombre del tipo de evento guardado, para que el selector lo muestre. */
  private async resolverTipoEvento(): Promise<void> {
    if (!this.edit?.idListadoCategoria) return;
    try {
      const cat = await this.listadoCategoriasService.getById(
        this.edit.idListadoCategoria,
      );
      this.listadoCategoriaInitialNames = cat?.nombre ? [cat.nombre] : [];
    } catch {
      this.listadoCategoriaInitialNames = [];
    }
  }

  async ngOnInit(): Promise<void> {
    this.edit = this.dialogData?.edit;
    this.createForm();
    this.configurarBusquedaVehiculos();
    await Promise.all([
      this.listarGrupos(),
      this.listarUbicaciones(),
      this.listarCategorias(),
      this.listarUsuarios(),
      this.resolverTipoEvento(),
    ]);
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }
}
